import React, { memo } from 'react';
import {
  Alert,
  Box,
  Button,
  Header,
  SpaceBetween,
  StatusIndicator,
  Table,
} from '@amzn/awsui-components-core';

import { BatchUnpublishPageViewModel, ExecutionResult } from './BatchUnpublishPageViewModel';

interface ResultsStepViewProps {
  readonly vm: BatchUnpublishPageViewModel;
}

function statusCell(item: ExecutionResult) {
  switch (item.status) {
    case 'queued':
      return <StatusIndicator type="pending">Queued</StatusIndicator>;
    case 'un-publishing':
      return <StatusIndicator type="in-progress">Un-publishing</StatusIndicator>;
    case 'succeeded':
      return <StatusIndicator type="success">Succeeded</StatusIndicator>;
    case 'failed':
      return <StatusIndicator type="error">{item.error ?? 'Failed'}</StatusIndicator>;
  }
}

export const ResultsStepView = memo(function ResultsStepView({ vm }: ResultsStepViewProps) {
  const isComplete = vm.step === 'results';
  const successCount = vm.results.filter((r) => r.status === 'succeeded').length;
  const failureCount = vm.results.filter((r) => r.status === 'failed').length;

  return (
    <SpaceBetween size="m">
      {isComplete && failureCount === 0 && (
        <Alert type="success">
          All {successCount} item{successCount !== 1 ? 's' : ''} unpublished successfully.
        </Alert>
      )}
      {isComplete && failureCount > 0 && (
        <Alert type="error">
          {failureCount} item{failureCount !== 1 ? 's' : ''} failed to unpublish.
          {successCount > 0 && ` ${successCount} succeeded.`}
        </Alert>
      )}

      <Table
        items={vm.results.slice()}
        trackBy="contentId"
        columnDefinitions={[
          { id: 'title', header: 'Title', cell: (item) => item.title },
          { id: 'contentId', header: 'Content ID', cell: (item) => item.contentId },
          { id: 'status', header: 'Status', cell: statusCell },
        ]}
        header={
          <Header variant="h2" counter={`(${vm.progressCurrent}/${vm.progressTotal})`}>
            Results
          </Header>
        }
        empty={<Box textAlign="center">No results.</Box>}
      />

      {isComplete && (
        <SpaceBetween direction="horizontal" size="s">
          <Button onClick={vm.startOver}>Start over</Button>
          {failureCount > 0 && (
            <Button variant="primary" onClick={vm.retryFailed}>
              Retry failed ({failureCount})
            </Button>
          )}
        </SpaceBetween>
      )}
    </SpaceBetween>
  );
});
